import { deepEqual as equal } from 'fast-equals'
import { MakeLogicType, actions, connect, kea, key, listeners, path, props, reducers, selectors } from 'kea'
import posthog from 'posthog-js'

import { FEATURE_FLAGS } from 'lib/constants'
import { copyToClipboard } from 'lib/utils/copyToClipboard'
import { removeUndefinedAndNull } from 'lib/utils/objects'
import { Scene } from 'scenes/sceneTypes'
import { urls } from 'scenes/urls'

import { SIDE_PANEL_CONTEXT_KEY, SidePanelSceneContext } from '~/layout/navigation-3000/sidepanel/types'
import { DataVisualizationNode, FileSystemIconType, HogQLFilters, NodeKind } from '~/queries/schema/schema-general'
import { Breadcrumb } from '~/types'

import type { FeatureFlagsSet } from '../../../lib/logic/featureFlagLogic'
import type { DataWarehouseSavedQuery, LinkBreadcrumb, QueryBasedInsightModel } from '../../../types'
import { normalizeFiltersForUrl, sqlEditorLogic, toDataVisualizationNode } from './sqlEditorLogic'
import type { QueryTab, SqlEditorSource } from './sqlEditorLogic'

export interface SaveAsMenuItem {
    action: 'insight' | 'endpoint' | 'view' | 'metric'
    label: string
    dataAttr?: string
}

function setFiltersHashParam(url: URL, filters: HogQLFilters | null | undefined): void {
    const normalizedFilters = normalizeFiltersForUrl(filters)
    if (!normalizedFilters) {
        return
    }

    const hashParams = new URLSearchParams(url.hash ? url.hash.slice(1) : '')
    hashParams.set('filters', JSON.stringify(normalizedFilters))
    url.hash = hashParams.toString()
}

export const renderTableCount = (count: undefined | number): null | JSX.Element => {
    if (!count) {
        return null
    }

    return (
        <span className="text-xs mr-1 italic text-[color:var(--color-text-secondary-3000)]">
            {`(${new Intl.NumberFormat('en', {
                notation: 'compact',
                compactDisplay: 'short',
            })
                .format(count)
                .toLowerCase()})`}
        </span>
    )
}

export interface EditorSceneLogicProps {
    tabId: string
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface editorSceneLogicValues {
    activeTab: QueryTab | null // sqlEditorLogic
    dashboardId: number | null // sqlEditorLogic
    dataLogicKey: string // sqlEditorLogic
    editingInsight: QueryBasedInsightModel | null // sqlEditorLogic
    editingView: DataWarehouseSavedQuery | undefined // sqlEditorLogic
    editorSource: SqlEditorSource // sqlEditorLogic
    featureFlags: FeatureFlagsSet // sqlEditorLogic
    insightLoading: boolean // sqlEditorLogic
    queryInput: string | null // sqlEditorLogic
    sourceQuery: DataVisualizationNode // sqlEditorLogic
    viewLoading: boolean // sqlEditorLogic
    breadcrumbs: Breadcrumb[]
    isHistoryModalOpen: boolean
    saveAsMenuItems: {
        primary: SaveAsMenuItem
        secondary: SaveAsMenuItem[]
    }
    sidePanelContext: SidePanelSceneContext
    titleSectionProps:
        | {
              description: string
              forceBackTo: LinkBreadcrumb
              name: string
              resourceType: {
                  type: string
              }
          }
        | {
              description?: undefined
              forceBackTo: LinkBreadcrumb
              name: string
              resourceType: {
                  type: string
              }
          }
        | {
              description?: undefined
              forceBackTo?: undefined
              name: string
              resourceType: {
                  type: string
              }
          }
    updateInsightButtonEnabled: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface editorSceneLogicActions {
    closeHistoryModal: () => {
        value: true
    }
    openHistoryModal: () => {
        value: true
    }
    shareTab: () => {
        value: true
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface editorSceneLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        breadcrumbs: (activeTab: QueryTab | null) => Breadcrumb[]
        titleSectionProps: (
            editingInsight: QueryBasedInsightModel<
                import('~/queries/schema/schema-general').Node<Record<string, any>>
            > | null,
            insightLoading: boolean,
            editingView: DataWarehouseSavedQuery | undefined,
            viewLoading: boolean,
            editorSource: SqlEditorSource,
            dashboardId: number | null,
            activeTab: QueryTab | null
        ) =>
            | {
                  description: string
                  forceBackTo: LinkBreadcrumb
                  name: string
                  resourceType: {
                      type: string
                  }
              }
            | {
                  description?: undefined
                  forceBackTo: LinkBreadcrumb
                  name: string
                  resourceType: {
                      type: string
                  }
              }
            | {
                  description?: undefined
                  forceBackTo?: undefined
                  name: string
                  resourceType: {
                      type: string
                  }
              }
        saveAsMenuItems: (
            editorSource: SqlEditorSource,
            dashboardId: number | null,
            featureFlags: FeatureFlagsSet
        ) => {
            primary: SaveAsMenuItem
            secondary: SaveAsMenuItem[]
        }
        updateInsightButtonEnabled: (
            sourceQuery: DataVisualizationNode,
            activeTab: QueryTab | null,
            editingInsight: QueryBasedInsightModel<
                import('~/queries/schema/schema-general').Node<Record<string, any>>
            > | null
        ) => boolean
    }
}

export type editorSceneLogicType = MakeLogicType<
    editorSceneLogicValues,
    editorSceneLogicActions,
    EditorSceneLogicProps,
    editorSceneLogicMeta
>

export const editorSceneLogic = kea<editorSceneLogicType>([
    path(['data-warehouse', 'editor', 'editorSceneLogic']),
    props({} as EditorSceneLogicProps),
    key((props) => props.tabId),
    connect((props: EditorSceneLogicProps) => ({
        values: [
            sqlEditorLogic({ tabId: props.tabId }),
            [
                'activeTab',
                'dashboardId',
                'dataLogicKey',
                'editingInsight',
                'editingView',
                'editorSource',
                'featureFlags',
                'insightLoading',
                'queryInput',
                'sourceQuery',
                'viewLoading',
            ],
        ],
    })),
    actions({
        shareTab: true,
        openHistoryModal: true,
        closeHistoryModal: true,
    }),
    reducers({
        isHistoryModalOpen: [
            false as boolean,
            {
                openHistoryModal: () => true,
                closeHistoryModal: () => false,
            },
        ],
    }),
    selectors({
        breadcrumbs: [
            (s) => [s.activeTab],
            (activeTab: null | import('./sqlEditorLogic').QueryTab): Breadcrumb[] => {
                const { draft, insight, view } = activeTab || {}
                const first = {
                    key: Scene.SQLEditor,
                    name: 'SQL query',
                    to: urls.sqlEditor(),
                    iconType: 'sql_editor' as FileSystemIconType,
                }
                if (view) {
                    return [
                        {
                            key: view.id,
                            name: view.name,
                            path: urls.sqlEditor({ view_id: view.id }),
                            iconType: 'sql_editor',
                        },
                    ]
                } else if (insight) {
                    return [
                        first,
                        {
                            key: insight.id,
                            name: insight.name || insight.derived_name || 'Untitled',
                            path: urls.sqlEditor({
                                insightShortId: insight.short_id,
                            }),
                            iconType: 'sql_editor',
                        },
                    ]
                } else if (draft) {
                    return [
                        first,
                        {
                            key: draft.id,
                            name: draft.name || 'Untitled',
                            path: urls.sqlEditor({ draftId: draft.id }),
                            iconType: 'sql_editor',
                        },
                    ]
                }
                return [first]
            },
        ],
        titleSectionProps: [
            (s) => [
                s.editingInsight,
                s.insightLoading,
                s.editingView,
                s.viewLoading,
                s.editorSource,
                s.dashboardId,
                s.activeTab,
            ],
            (
                editingInsight: null | import('~/types').QueryBasedInsightModel,
                insightLoading: boolean,
                editingView: import('~/types').DataWarehouseSavedQuery | undefined,
                viewLoading: boolean,
                editorSource: import('./sqlEditorLogic').SqlEditorSource,
                dashboardId: number | null,
                activeTab: null | import('./sqlEditorLogic').QueryTab
            ) => {
                if (editingInsight) {
                    const forceBackTo: Breadcrumb = dashboardId
                        ? {
                              key: 'dashboard',
                              name: 'Back to dashboard',
                              path: urls.dashboard(dashboardId),
                              iconType: 'dashboard',
                          }
                        : {
                              key: editingInsight.short_id,
                              name: 'Back to insight',
                              path: urls.insightView(editingInsight.short_id),
                              iconType: 'insight/hog',
                          }

                    return {
                        forceBackTo,
                        name: activeTab?.name || editingInsight.derived_name || '',
                        description: activeTab?.description ?? editingInsight.description ?? '',
                        resourceType: { type: 'insight/hog' },
                    }
                }

                if (insightLoading) {
                    return {
                        name: 'Loading insight...',
                        resourceType: { type: 'insight/hog' },
                    }
                }

                if (editingView) {
                    return {
                        name: editingView.name,
                        resourceType: {
                            type: editingView.is_materialized ? 'matview' : 'view',
                        },
                    }
                }

                if (viewLoading) {
                    return {
                        name: 'Loading view...',
                        resourceType: { type: 'view' },
                    }
                }

                if (!activeTab) {
                    const searchParams = new URLSearchParams(window.location.search)
                    const hashParams = new URLSearchParams(window.location.hash.slice(1))
                    if (searchParams.get('open_view') || hashParams.get('view')) {
                        return {
                            name: 'Loading view...',
                            resourceType: { type: 'view' },
                        }
                    }

                    if (searchParams.get('open_insight') || hashParams.get('insight')) {
                        return {
                            name: 'Loading insight...',
                            resourceType: { type: 'insight/hog' },
                        }
                    }
                }

                if (editorSource === 'endpoint') {
                    const forceBackTo: Breadcrumb = {
                        key: 'endpoints',
                        name: 'Endpoints',
                        path: urls.endpoints(),
                        iconType: 'endpoints',
                    }

                    return {
                        forceBackTo,
                        name: 'New endpoint',
                        resourceType: { type: 'sql_editor' },
                    }
                }

                if (editorSource === 'view') {
                    const forceBackTo: Breadcrumb = {
                        key: 'models',
                        name: 'Models',
                        path: urls.models(),
                        iconType: 'sql_editor',
                    }

                    return {
                        forceBackTo,
                        name: 'New view',
                        resourceType: { type: 'sql_editor' },
                    }
                }

                if (dashboardId) {
                    const forceBackTo: Breadcrumb = {
                        key: 'dashboard',
                        name: 'Back to dashboard',
                        path: urls.dashboard(dashboardId),
                        iconType: 'dashboard',
                    }

                    return {
                        forceBackTo,
                        name: 'New SQL query',
                        resourceType: { type: 'sql_editor' },
                    }
                }

                return {
                    name: 'New SQL query',
                    resourceType: { type: 'sql_editor' },
                }
            },
        ],
        saveAsMenuItems: [
            (s) => [s.editorSource, s.dashboardId, s.featureFlags],
            (
                editorSource: import('./sqlEditorLogic').SqlEditorSource,
                dashboardId: number | null,
                featureFlags: import('../../../lib/logic/featureFlagLogic').FeatureFlagsSet
            ): { primary: SaveAsMenuItem; secondary: SaveAsMenuItem[] } => {
                const endpointsEnabled = !!featureFlags[FEATURE_FLAGS.ENDPOINTS]
                const metricsEnabled = !!featureFlags[FEATURE_FLAGS.PRODUCT_DATA_CATALOG]
                const saveAsInsightItem: SaveAsMenuItem = {
                    action: 'insight',
                    label: dashboardId ? 'Save & add to dashboard' : 'Save as insight',
                }
                const saveAsEndpointItem: SaveAsMenuItem = {
                    action: 'endpoint',
                    label: 'Save as endpoint',
                }
                const saveAsViewItem: SaveAsMenuItem = {
                    action: 'view',
                    label: 'Save as view',
                    dataAttr: 'sql-editor-save-view-button',
                }
                const saveAsMetricItem: SaveAsMenuItem = {
                    action: 'metric',
                    label: 'Save as metric',
                }
                const extraItems = [
                    ...(endpointsEnabled ? [saveAsEndpointItem] : []),
                    ...(metricsEnabled ? [saveAsMetricItem] : []),
                ]

                if (editorSource === 'metric' && metricsEnabled) {
                    return {
                        primary: saveAsMetricItem,
                        secondary: [
                            saveAsInsightItem,
                            saveAsViewItem,
                            ...(endpointsEnabled ? [saveAsEndpointItem] : []),
                        ],
                    }
                }

                if (editorSource === 'endpoint' && endpointsEnabled) {
                    return {
                        primary: saveAsEndpointItem,
                        secondary: [saveAsInsightItem, saveAsViewItem, ...(metricsEnabled ? [saveAsMetricItem] : [])],
                    }
                }

                if (editorSource === 'view') {
                    return {
                        primary: saveAsViewItem,
                        secondary: [saveAsInsightItem, ...extraItems],
                    }
                }

                return {
                    primary: saveAsInsightItem,
                    secondary: [saveAsViewItem, ...extraItems],
                }
            },
        ],
        updateInsightButtonEnabled: [
            (s) => [s.sourceQuery, s.activeTab, s.editingInsight],
            (
                sourceQuery: DataVisualizationNode,
                activeTab: null | import('./sqlEditorLogic').QueryTab,
                editingInsight: null | import('~/types').QueryBasedInsightModel
            ) => {
                if (!editingInsight?.query) {
                    return false
                }

                const updatedName = activeTab?.name !== editingInsight.name
                const updatedDescription = (activeTab?.description ?? '') !== (editingInsight.description ?? '')
                const sourceQueryWithoutUndefinedAndNullKeys = removeUndefinedAndNull(sourceQuery)
                // Normalize so DataTableNode-based insights don't look "changed" immediately after load.
                const editingInsightQuery = toDataVisualizationNode(editingInsight.query) ?? editingInsight.query

                return (
                    updatedName ||
                    updatedDescription ||
                    !equal(sourceQueryWithoutUndefinedAndNullKeys, removeUndefinedAndNull(editingInsightQuery))
                )
            },
        ],
    }),
    listeners(({ values }) => ({
        openHistoryModal: () => {
            posthog.capture('sql-editor-history-modal-opened', {
                object: values.editingView ? 'view' : 'insight',
            })
        },
        shareTab: () => {
            const { activeTab, queryInput, sourceQuery } = values
            if (!activeTab) {
                return
            }

            const currentUrl = new URL(window.location.href)
            const shareUrl = new URL(currentUrl.origin + currentUrl.pathname)

            if (activeTab.insight) {
                shareUrl.searchParams.set('open_insight', activeTab.insight.short_id)

                if (activeTab.insight.query?.kind === NodeKind.DataVisualizationNode) {
                    const query = (activeTab.insight.query as DataVisualizationNode).source.query
                    if (queryInput !== query) {
                        shareUrl.searchParams.set('open_query', queryInput ?? '')
                    }
                }
            } else if (activeTab.view) {
                shareUrl.searchParams.set('open_view', activeTab.view.id)

                if (queryInput !== activeTab.view.query?.query) {
                    shareUrl.searchParams.set('open_query', queryInput ?? '')
                }
            } else {
                shareUrl.searchParams.set('open_query', queryInput ?? '')
            }

            setFiltersHashParam(shareUrl, sourceQuery.source.filters)

            void copyToClipboard(shareUrl.toString(), 'share link')
        },
    })),
    selectors({
        [SIDE_PANEL_CONTEXT_KEY]: [
            () => [],
            (): SidePanelSceneContext => ({
                access_control_resource: 'warehouse_objects',
            }),
        ],
    }),
])
